'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Select from 'react-select';
import { useRouter, useSearchParams } from 'next/navigation';
import { ApplicableSubscriptionsReadOnly } from 'hotdesk_typescript';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';
import { NumberFormatService } from '@/hotdesk-ui/services/number-format.service';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { tracking } from '@/hotdesk-ui/services/tracking.service';
import { getProviderTrackingProps } from '@/hotdesk-ui/utils/provider';
import { getSubscriptionPromoDetails, resolveInitialPriceUnit, PRICE_UNIT_ORDER, getMembershipDiscountedUnitPrice } from '@/hotdesk-ui/utils/listingSubscriptionUtils';
import { usePreferredUserSubscriptionIds } from '@/hotdesk-ui/hooks/usePreferredUserSubscriptionIds';
import { getActiveUserSubscriptions } from '@/hotdesk-ui/utils/subscriptionUtils';
import HotdeskButton from '@/hotdesk-ui/components/shared/HotdeskButton';
import ScrollablePriceText from '@/hotdesk-ui/components/shared/ScrollablePriceText';
import WorkScheduleModal from './WorkScheduleModal';
import PlanUnitOption from './PlanUnitOption';
import styles from './MobileStickyBar.module.scss';
export interface MobileStickyBarProps {
  variant: 'workspace' | 'listing';
  visible: boolean;
  listing?: Record<string, any>;
  applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null;
  onViewListings?: () => void;
  onContactUs?: () => void;
  subscription?: string | null;
  price_unit?: string;
  setPrice_unit?: (value: string) => void;
  workingHoursToday?: {
    dayLabel: string;
    hours: string;
  };
}
const PRICE_ORDER = [...PRICE_UNIT_ORDER];
const MobileStickyBar: FC<MobileStickyBarProps> = ({
  variant,
  visible,
  listing,
  applicableSubscriptions,
  onViewListings,
  onContactUs,
  subscription,
  price_unit = '',
  setPrice_unit,
  workingHoursToday
}) => {
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const frequencyParam = searchParams.get('frequency');
  const currencyList = useAppSelector((state: RootState) => state.currency?.currency);
  const defaultCurrency = useAppSelector((state: RootState) => state.currency?.defaultCurrency);
  const languageCode = useAppSelector((state: RootState) => state.listing?.defaultLanguage);
  const user = useAppSelector((state: RootState) => state.auth.user);
  const preferredIds = usePreferredUserSubscriptionIds();
  const fallbackUserSubs = useMemo(() => getActiveUserSubscriptions(user) as unknown as Record<string, unknown>[], [user]);
  const priceObj = listing?.rules?.price || {};
  const timeKeys = PRICE_ORDER.filter(k => k in priceObj);
  const timeOptions = timeKeys.map(key => ({
    value: key,
    label: t('workspace-details.' + key)
  }));
  const {
    appliesFor,
    cap: promoCap,
    packageName
  } = useMemo(() => getSubscriptionPromoDetails(applicableSubscriptions, listing?.type, preferredIds, fallbackUserSubs), [applicableSubscriptions, listing?.type, preferredIds, fallbackUserSubs]);
  const resolveSelectedUnit = useMemo(() => {
    return (unit?: string | null) => resolveInitialPriceUnit({
      priceObj,
      frequencyParam: unit,
      preferredUnit: price_unit || null,
      appliesFor
    });
  }, [priceObj, appliesFor, price_unit]);
  const [selectedUnit, setSelectedUnit] = useState(() => resolveSelectedUnit(frequencyParam || price_unit || null));
  const [displayPrice, setDisplayPrice] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [currencyName, setCurrencyName] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [useMembership, setUseMembership] = useState(true);
  const barRef = useRef<HTMLDivElement>(null);
  const unitHasSubscription = !!subscription;
  const membershipOn = unitHasSubscription && useMembership;
  useEffect(() => {
    if (unitHasSubscription) {
      setUseMembership(true);
    }
  }, [listing?.id, unitHasSubscription]);
  useEffect(() => {
    setSelectedUnit(resolveInitialPriceUnit({
      priceObj,
      frequencyParam,
      appliesFor
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id, frequencyParam]);
  useEffect(() => {
    if (!defaultCurrency?.code || !priceObj[selectedUnit]) return;
    const converted = currencyConvert(currencyList, listing?.rules?.currency as string, defaultCurrency.code, priceObj[selectedUnit]);
    let convertedCap: number | null = null;
    if (promoCap != null) {
      convertedCap = currencyConvert(currencyList, listing?.rules?.currency as string, defaultCurrency.code, promoCap);
    }
    const formatter = new NumberFormatService();
    setDisplayPrice(formatter.formatNumberWithCountingLabel(converted, languageCode));
    setCurrencyName(defaultCurrency.name || defaultCurrency.code);
    setDiscountedPrice(getMembershipDiscountedUnitPrice(converted, convertedCap));
  }, [defaultCurrency, currencyList, selectedUnit, listing, promoCap, languageCode, priceObj]);
  useEffect(() => {
    setPrice_unit?.(selectedUnit);
  }, [selectedUnit, setPrice_unit]);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!visible) {
      document.body.style.setProperty('--bottom-cta-offset', '0px');
      return;
    }
    const bar = barRef.current;
    if (!bar) return;
    const apply = () => {
      document.body.style.setProperty('--bottom-cta-offset', `${bar.offsetHeight}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [visible]);
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.removeProperty('--bottom-cta-offset');
      }
    };
  }, []);
  const handleBookNow = () => {
    if (!user) firebaseAnalytics('book_now_clicked_no_login');else firebaseAnalytics('book_now_clicked');
    firebaseAnalytics('web_booking_unit_selected', {
      web_booking_unit: selectedUnit
    });
    if (listing?.id && typeof window !== 'undefined') {
      tracking.onReviewBookingClicked(listing?.workspace?.id ?? '', listing.id, window.location.href, getProviderTrackingProps(listing));
      tracking.onBookingUnitSelected(listing.id, window.location.href, getProviderTrackingProps(listing));
    }
    const params = new URLSearchParams();
    if (useMembership && unitHasSubscription) {
      const planForBooking = subscription || packageName || '';
      if (planForBooking) params.set('subscription', planForBooking);
    }
    const qs = params.toString();
    router.push(`/booking/${listing?.id}/${selectedUnit}${qs ? `?${qs}` : ''}`);
  };
  const renderMembershipToggle = () => {
    if (!unitHasSubscription) return null;
    return <div className={styles.membershipToggleRow} data-sentry-component="renderMembershipToggle" data-sentry-source-file="MobileStickyBar.tsx">
                <span className={styles.membershipToggleLabel}>
                    {t('workspace-details.useMyMembership')}
                </span>
                <div className={styles.membershipToggleControl}>
                    <button type="button" className={styles.membershipToggle} data-on={membershipOn} onClick={() => setUseMembership(prev => !prev)} role="switch" aria-checked={membershipOn} aria-label={t('workspace-details.useMyMembership')}>
                        <span className={styles.membershipToggleKnob} />
                    </button>
                </div>
            </div>;
  };
  const renderPrice = () => {
    if (membershipOn && discountedPrice === 0) {
      return <div className={styles.priceRow}>
                    <ScrollablePriceText title={`${currencyName} 0`}>
                        <span className={styles.priceValue}>
                            {currencyName} 0
                        </span>
                    </ScrollablePriceText>
                    <span className={styles.priceUnit}>
                        / {t('workspace-details.' + selectedUnit)}
                    </span>
                </div>;
    }
    if (membershipOn && discountedPrice > 0) {
      return <div className={`${styles.priceRow} ${styles.priceRowDiscounted}`}>
                    <span className={styles.priceStrikethrough}>
                        {currencyName} {displayPrice}
                    </span>
                    <span className={styles.priceValue}>
                        {discountedPrice.toFixed(2)}
                    </span>
                    <span className={styles.priceUnit}>
                        {currencyName} / {t('workspace-details.' + selectedUnit)}
                    </span>
                </div>;
    }
    return <div className={styles.priceRow} data-sentry-component="renderPrice" data-sentry-source-file="MobileStickyBar.tsx">
                <ScrollablePriceText title={`${currencyName} ${displayPrice}`} data-sentry-element="ScrollablePriceText" data-sentry-source-file="MobileStickyBar.tsx">
                    <span className={styles.priceValue}>
                        {currencyName} {displayPrice}
                    </span>
                </ScrollablePriceText>
                <span className={styles.priceUnit}>
                    / {t('workspace-details.' + selectedUnit)}
                </span>
            </div>;
  };
  return <div ref={barRef} className={`${styles.bar} ${visible ? styles.visible : ''}`} role="toolbar" aria-label="Booking actions" data-sentry-component="MobileStickyBar" data-sentry-source-file="MobileStickyBar.tsx">
            <div className={styles.inner}>
                <div className={styles.topRow}>
                    <div className={styles.priceInfo}>
                        {renderMembershipToggle()}
                        {!unitHasSubscription && <span className={styles.priceLabel}>
                                {t('workspace-details.startingFrom')}
                            </span>}
                        {renderPrice()}
                    </div>
                    {variant === 'workspace' && workingHoursToday?.hours && <button type="button" className={styles.hoursInfo} onClick={() => setScheduleOpen(true)} aria-label={t('workspace-details.workSchedule')}>
                            <Clock size={16} />
                            <div>
                                <span className={styles.hoursLabel}>{t('workspace-details.openNow')}</span>
                                <span className={styles.hoursValue}>{workingHoursToday.hours}</span>
                            </div>
                        </button>}
                    {variant === 'listing' && timeOptions.length > 1 && <div className={styles.miniSelect}>
                            <Select value={timeOptions.find(o => o.value === selectedUnit) ?? timeOptions[0]} options={timeOptions} isSearchable={false} menuPlacement="top" classNamePrefix="mobile-select" onChange={opt => {
            if (!opt) return;
            setSelectedUnit(opt.value);
            setPrice_unit?.(opt.value);
          }} formatOptionLabel={option => <PlanUnitOption unit={option.value} label={option.label} iconSize={14} />} />
                        </div>}
                </div>

                <div className={styles.ctaRow}>
                    {variant === 'workspace' ? <>
                            <HotdeskButton variant="secondary" size="md" shape="pill" fullWidth onClick={onContactUs}>
                                {t('workspace-details.contactUs')}
                            </HotdeskButton>
                            <HotdeskButton variant="primary" size="md" shape="pill" fullWidth onClick={onViewListings}>
                                {t('workspace-details.viewListings')}
                            </HotdeskButton>
                        </> : <>
                            <HotdeskButton variant="secondary" size="md" shape="pill" fullWidth onClick={onContactUs}>
                                {t('workspace-details.contactUs')}
                            </HotdeskButton>
                            <HotdeskButton variant="primary" size="md" shape="pill" fullWidth onClick={handleBookNow}>
                                {t('header.book_btn')}
                            </HotdeskButton>
                        </>}
                </div>
            </div>

            <WorkScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} workingHours={listing?.rules?.workingHours} data-sentry-element="WorkScheduleModal" data-sentry-source-file="MobileStickyBar.tsx" />
        </div>;
};
export default MobileStickyBar;